import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCalendarUserInfo } from "../../../../api/appointments/calendar-management";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import {
  setUserInfo,
  updateCalendarInfo,
} from "../../../../redux/actions/CalendarManagementActions";
import { validateCalendar } from "../../../../services-logic/calendar-management/calendar-management-logic";
import { postOpenModal } from "../../../../utils/utilPostMessage";
import ConnectedEmail from "./ConnectedEmail";

const ConnectedToCalendarButtons = ({ onConnectToCalendar }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const { calendarFound, userInfo, calendarInfo, updatedCalendarInfo } = useSelector(
    (state) => state.calendarManagement
  );
  const { organizationId, accountId, externalOverlay } = useSelector((state) => state.info);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const [modalStyles, setModalStyles] = useState("");
  const disconnectRef = useRef(null);

  const disconnectCalendar = () => {
    dispatch(updateCalendarInfo({ ...updatedCalendarInfo, id: null }));
    dispatch(setUserInfo(null));
    // Use it if we want to clear the appointment types and scheduling windows on disconnect
    // dispatch(updateAppointmentTypes([]));
    // dispatch(updateSchedulingWindows([]));
  };

  const DISCONNECT_BODY_MESSAGE = "Are you sure you want to disconnect your calendar?";

  const onDisconnect = () => {
    if (externalOverlay) {
      const message = {
        childrenType: "confirmation",
        id: "disconnect-calendar",
        headerTitle: "Disconnect calendar",
        bodyMessage: DISCONNECT_BODY_MESSAGE,
        cancelText: "Cancel",
        confirmText: "Disconnect",
      };
      postOpenModal(message);
    } else {
      if (
        disconnectRef &&
        disconnectRef.current &&
        typeof disconnectRef.current.getBoundingClientRect === "function"
      ) {
        const accordionRect = disconnectRef.current.getBoundingClientRect();

        setModalStyles({
          position: "absolute",
          top: `${accordionRect.top + 20}px `,
          left: "50%",
          transform: "translateX(-50%)",
        });
      } else {
        setModalStyles({});
      }

      setIsConfirmationModalOpen(true);
    }
  };

  useEffect(() => {
    if (externalOverlay) {
      const handleMessage = (event) => {
        const data = event.data;
        if (data.type === "overlay-to-snippet") {
          const extra = data.extra;
          if (extra.id === "disconnect-calendar" && extra.action === "confirm") {
            disconnectCalendar();
          }
        }
      };

      // Add the event listener
      window.addEventListener("message", handleMessage);

      // Clean up the event listener on component unmount or when externalOverlay changes
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [externalOverlay, disconnectCalendar]);

  const onOpenUpdateCalendarList = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const closeDropdown = (event) => {
      if (!event.target.closest("#calendar-list-button")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", closeDropdown);

    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, []);

  const getUserInfo = async () => {
    const aliasName = updatedCalendarInfo.aliasName;
    const calendarProvider = updatedCalendarInfo.provider;

    const userInfo = await getCalendarUserInfo({
      aliasName,
      calendarProvider,
      organizationId,
      accountId,
    });
    dispatch(setUserInfo(userInfo));
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      setIsLoading(true);
      await getUserInfo();
      setIsLoading(false);
    };
    fetchUserInfo();
  }, [updatedCalendarInfo]);

  const connectedCalendarName = updatedCalendarInfo.provider === "google" ? "Google" : "Outlook";
  const isCalendarValid = validateCalendar();

  return (
    <div ref={disconnectRef} className="flex flex-col gap-2">
      <div className="w-full flex gap-2 items-start md:items-center flex-col md:flex-row">
        <ConnectedEmail isLoading={isLoading} isCalendarValid={isCalendarValid} />
        <div className="flex gap-2 items-center relative">
          <button
            onClick={() => onConnectToCalendar(updatedCalendarInfo.provider, "update")}
            className="text-primary hover:underline text-[12px] md:text-[14px]"
          >
            Update {connectedCalendarName} Calendar
          </button>
          <button
            id="calendar-list-button"
            className="text-primary hover:underline text-[12px] md:text-[14px]"
            onClick={onOpenUpdateCalendarList}
          >
            <svg
              className={`size-6 transition-transform duration-500 ease-in-out transform`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div className="w-[2px] h-3 bg-slate-300"></div>
          <button
            className="text-primary hover:underline text-[12px] md:text-[14px]"
            onClick={onDisconnect}
          >
            Disconnect
          </button>
          {isOpen && (
            <div className="absolute flex flex-col -left-[1%] top-[110%] bg-white justify-start items-start shadow-lg rounded-md border">
              <button
                onClick={() => onConnectToCalendar("google", "update")}
                className="w-full text-primary text-start hover:bg-slate-200 p-2 text-[12px] md:text-[14px]"
              >
                Update Google Calendar
              </button>
              <button
                onClick={() => onConnectToCalendar("microsoft", "update")}
                className="text-primary hover:bg-slate-200 p-2 text-[12px] md:text-[14px] lg:text-[16px]"
              >
                Update Outlook Calendar
              </button>
            </div>
          )}
        </div>
      </div>
      {!isLoading && !isCalendarValid && (
        <div className="flex">
          <span className="text-[12px] md:text-[14px] lg:text-[16px] text-red-600 font-semibold">
            Your calendar is disconnected, appointments cannot be set. Reconnect it by clicking
            “Update Calendar”.
          </span>
        </div>
      )}

      {!externalOverlay && isConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          dataFromSnippet={{
            headerTitle: "Disconnect calendar",
            bodyMessage: DISCONNECT_BODY_MESSAGE,
            cancelText: "Cancel",
            confirmText: "Disconnect",
          }}
          onConfirm={(event) => disconnectCalendar()}
          onCancel={() => setIsConfirmationModalOpen(false)}
          modalStyles={modalStyles}
        />
      )}
    </div>
  );
};

export default ConnectedToCalendarButtons;
